import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Textarea,
} from "@fluentui/react-components";
import { CheckmarkRegular, DismissRegular } from "@fluentui/react-icons";
import { DialogFooterAction } from "components/dialogFooterAction/DialogFooterAction";
import { useEffect, useState } from "react";
import {
  useSharedDialogContentStyles,
  useSharedDialogFooterStyles,
} from "theme/sharedStyles";
import { IAddStakeholderDialogProps } from "./AddStakeholderDialog.types";

export const AddStakeholderDialog = ({
  isOpen,
  onDismiss,
  onConfirm,
  initialValues,
}: IAddStakeholderDialogProps): JSX.Element => {
  const contentStyles = useSharedDialogContentStyles();
  const footerStyles = useSharedDialogFooterStyles();
  const [name, setName] = useState("");
  const [roleTitle, setRoleTitle] = useState("");
  const [details, setDetails] = useState("");
  const [nameError, setNameError] = useState("");
  const [roleTitleError, setRoleTitleError] = useState("");

  const isEditing = !!initialValues;

  useEffect(() => {
    if (isOpen) {
      setName(initialValues?.name ?? "");
      setRoleTitle(initialValues?.roleTitle ?? "");
      setDetails(initialValues?.details ?? "");
      setNameError("");
      setRoleTitleError("");
    }
  }, [isOpen, initialValues]);

  const handleConfirm = () => {
    let hasError = false;
    if (!name.trim()) {
      setNameError("Name is required");
      hasError = true;
    }
    if (!roleTitle.trim()) {
      setRoleTitleError("Role / Title is required");
      hasError = true;
    }
    if (hasError) return;

    onConfirm({
      id: initialValues?.id ?? crypto.randomUUID(),
      name: name.trim(),
      roleTitle: roleTitle.trim(),
      details: details.trim() || undefined,
    });
  };

  const handleDismiss = () => {
    onDismiss();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(_, data) => {
        if (!data.open) handleDismiss();
      }}
    >
      <DialogSurface className={contentStyles.dialogSurface}>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular aria-hidden="true" />}
                aria-label="Close dialog"
                onClick={handleDismiss}
              />
            }
          >
            {isEditing ? "Edit stakeholder" : "Add stakeholder"}
          </DialogTitle>
          <DialogContent>
            <div className={contentStyles.formContainer}>
              <Field
                label="Name"
                required
                validationState={nameError ? "error" : "none"}
                validationMessage={nameError || undefined}
              >
                <Input
                  value={name}
                  onChange={(_e, data) => {
                    setName(data.value);
                    if (nameError) setNameError("");
                  }}
                  placeholder="e.g. Jane Smith"
                  autoFocus={!isEditing}
                  aria-label="Stakeholder name"
                />
              </Field>
              <Field
                label="Role / Title"
                required
                validationState={roleTitleError ? "error" : "none"}
                validationMessage={roleTitleError || undefined}
              >
                <Input
                  value={roleTitle}
                  onChange={(_e, data) => {
                    setRoleTitle(data.value);
                    if (roleTitleError) setRoleTitleError("");
                  }}
                  placeholder="e.g. VP of Operations"
                  aria-label="Role or title"
                />
              </Field>
              <Field label="Details (optional)">
                <Textarea
                  value={details}
                  onChange={(_e, data) => setDetails(data.value)}
                  placeholder="e.g. Approve project scope, attend steering committee"
                  aria-label="Stakeholder details"
                  resize="vertical"
                />
              </Field>
            </div>
          </DialogContent>
        </DialogBody>
        <div className={footerStyles.dialogFooter}>
          <DialogFooterAction
            icon={<CheckmarkRegular />}
            tooltip={isEditing ? "Save" : "Add stakeholder"}
            dimmed={!name.trim() || !roleTitle.trim()}
            onClick={handleConfirm}
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};
